/**
 * The pool Settings "Pool" card: the name (rename) and the banner (R2, D3).
 * Fills the `pool` slot in `poolSettingsState.ts` (`ownedBy: "ISSUE-201"`).
 *
 * The name control was the missing half. `/pool/update` has always accepted a
 * `name`, but no screen sent one, so a typo at creation was permanent. The
 * icon and color controls named in `27-pool-settings.md` §27.2 card 1 are
 * still not built here: unlike the name they have no endpoint at all, and
 * they belong to a separate card.
 *
 * Name first, banner second. The name is the thing every other member sees in
 * their pools list, and it is the one a Commissioner comes to this card to
 * fix.
 */
import { useBannerSet } from "@sportsmet/pool-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  POOL_BANNER_REMOVE_CONFIRM_BODY,
  POOL_BANNER_REMOVE_CONFIRM_CANCEL,
  POOL_BANNER_REMOVE_CONFIRM_CONFIRM,
  POOL_BANNER_REMOVE_CONFIRM_TITLE,
} from "../poolBanner/poolBannerCopy";
import { PoolBannerField } from "../poolBanner/PoolBannerField";
import { PoolSettingsNameField } from "./PoolSettingsNameField";

export type typePoolSettingsPoolCardProps = {
  poolId: string;
  /** pool.name from POST /pool/get. Null while that read is in flight. */
  poolName: string | null;
  /** pool.bannerImageUrl from POST /pool/get, already a freshly signed URL. */
  bannerImageUrl: string | null;
};

export const PoolSettingsPoolCard = ({
  poolId,
  poolName,
  bannerImageUrl,
}: typePoolSettingsPoolCardProps): ReactNode => {
  const bannerSet = useBannerSet();

  async function attach(mediaId: string): Promise<void> {
    try {
      const result = await bannerSet.mutateAsync({
        poolId,
        bannerMediaId: mediaId,
      });
      if (showApiError(result)) return;
      // useBannerSet's own generated onSuccess invalidates usePoolGet for us
      // (its schema deps name "post /pool/get"), so no manual key is written
      // here.
    } catch {
      showOfflineError();
    }
  }

  async function remove(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: POOL_BANNER_REMOVE_CONFIRM_TITLE,
      body: POOL_BANNER_REMOVE_CONFIRM_BODY,
      cancelLabel: POOL_BANNER_REMOVE_CONFIRM_CANCEL,
      confirmLabel: POOL_BANNER_REMOVE_CONFIRM_CONFIRM,
    });
    if (!isConfirmed) return;

    try {
      const result = await bannerSet.mutateAsync({
        poolId,
        bannerMediaId: null,
      });
      if (showApiError(result)) return;
    } catch {
      showOfflineError();
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="pool-settings-pool-card">
      <PoolSettingsNameField poolId={poolId} poolName={poolName} />
      <PoolBannerField
        currentBannerUrl={bannerImageUrl}
        onBannerReady={(mediaId) => {
          void attach(mediaId);
        }}
        onRemove={() => {
          void remove();
        }}
        isSaving={bannerSet.isPending}
        testId="pool-settings-banner"
      />
    </div>
  );
};
